import React, { useCallback, useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, CircularProgress, Divider, Stack, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { apiSupportAgentConfig, apiSupportAgentConfigGuardar } from "../settings/supportApi";

const emptyConfig = {
  identity: { name: "", role: "", organization: "", language: "es-MX", description: "" },
  behavior: { tone: "", response_length: "" },
  rules: [],
  contacts: { name: "", phone: "", email: "", availability: "" },
  waiting_responses: { default: [], system_guidance: [], data_lookup: [], action_request: [], general: [] },
};

const asLines = (items) => Array.isArray(items) ? items.join("\n") : (items || "");
const fromLines = (value) => value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
const errorMessage = (error) => error.response?.data?.message || error.response?.data?.detail || error.message || "Ocurrió un error.";

export default function AgenteConfiguracionPage() {
  const [config, setConfig] = useState(emptyConfig);
  const [version, setVersion] = useState(0);
  const [modifiedAt, setModifiedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setLoaded(false);
    setNotice(null);
    try {
      const response = await apiSupportAgentConfig();
      setConfig({
        ...emptyConfig,
        ...(response?.config || {}),
        identity: { ...emptyConfig.identity, ...(response?.config?.identity || {}) },
        behavior: { ...emptyConfig.behavior, ...(response?.config?.behavior || {}) },
        contacts: { ...emptyConfig.contacts, ...(response?.config?.contacts || {}) },
        waiting_responses: { ...emptyConfig.waiting_responses, ...(response?.config?.waiting_responses || {}) },
      });
      setVersion(response?.version || 0);
      setModifiedAt(response?.modified_at_utc || "");
      setLoaded(true);
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const update = (section, field, value) => setConfig((current) => ({
    ...current,
    [section]: { ...current[section], [field]: value },
  }));

  const guardar = async () => {
    if (!loaded) return;
    setSaving(true);
    setNotice(null);
    try {
      const payload = {
        ...config,
        rules: Array.isArray(config.rules) ? config.rules.filter((item) => item.trim()) : fromLines(config.rules || ""),
        waiting_responses: Object.fromEntries(Object.entries(config.waiting_responses || {}).map(([key, values]) => [
          key,
          Array.isArray(values) ? values.filter((item) => item.trim()) : fromLines(values || ""),
        ])),
      };
      const response = await apiSupportAgentConfigGuardar(payload);
      setVersion(response?.version || version + 1);
      setModifiedAt(response?.modified_at_utc || new Date().toISOString());
      setNotice({ type: "success", text: "Configuración guardada y activa para las próximas conversaciones." });
    } catch (error) {
      setNotice({ type: "error", text: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  return <Box sx={{ maxWidth: 1100, mx: "auto", pb: 5 }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 2 }}>
      <Box>
        <Typography variant="h4" gutterBottom>Instrucciones del agente</Typography>
        <Typography color="text.secondary">Indica cómo debe responder y qué reglas debe seguir.</Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={cargar} disabled={saving}>Recargar</Button>
        <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={guardar} disabled={saving || !loaded}>
          {saving ? "Guardando…" : "Guardar instrucciones"}
        </Button>
      </Stack>
    </Stack>
    {notice && <Alert severity={notice.type} sx={{ mb: 2 }}>{notice.text}</Alert>}
    {version > 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
      Versión activa {version}{modifiedAt ? ` · Actualizada ${new Date(modifiedAt).toLocaleString()}` : ""}
    </Typography>}

    <Card sx={{ mb: 2 }}><CardContent>
      <Typography variant="h6" gutterBottom>¿Qué debe hacer el agente?</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Escribe una instrucción por renglón. Conserva las instrucciones existentes y agrega las nuevas debajo.
      </Typography>
      <TextField
        label="Instrucciones para el agente"
        placeholder={"Ejemplo: Distingue lo que dice el usuario de lo que responde el asistente.\nEjemplo: Explica los pasos del manual en orden."}
        value={asLines(config.rules)}
        onChange={(e) => setConfig((current) => ({ ...current, rules: e.target.value }))}
        helperText="Los cambios se aplican a las conversaciones que abras después de guardar."
        multiline minRows={8} fullWidth disabled={!loaded || saving}
      />
    </CardContent></Card>

    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle1">Otras opciones</Typography>
          <Typography variant="body2" color="text.secondary">Contacto de soporte, identidad y mensajes de espera.</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
    <Card sx={{ mb: 2 }}><CardContent>
      <Typography variant="h6" gutterBottom>Contacto de soporte</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Datos que el agente puede proporcionar cuando sea necesario contactar a soporte.</Typography>
      <Stack spacing={2}>
        <TextField label="Nombre del área de soporte" value={config.contacts.name} onChange={(e) => update("contacts", "name", e.target.value)} fullWidth />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <TextField label="Teléfono" value={config.contacts.phone} onChange={(e) => update("contacts", "phone", e.target.value)} fullWidth />
          <TextField label="Correo" value={config.contacts.email} onChange={(e) => update("contacts", "email", e.target.value)} fullWidth />
        </Box>
        <TextField label="Horario de atención" value={config.contacts.availability} onChange={(e) => update("contacts", "availability", e.target.value)} fullWidth />
      </Stack>
    </CardContent></Card>

    <Card sx={{ mb: 2 }}><CardContent>
      <Typography variant="h6" gutterBottom>Identidad y estilo</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Nombre, idioma y forma de responder del agente.</Typography>
      <Stack spacing={2}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
          <TextField label="Nombre del asistente" value={config.identity.name} onChange={(e) => update("identity", "name", e.target.value)} fullWidth />
          <TextField label="Idioma" value={config.identity.language} onChange={(e) => update("identity", "language", e.target.value)} fullWidth />
        </Box>
        <TextField label="Rol" value={config.identity.role} onChange={(e) => update("identity", "role", e.target.value)} fullWidth />
        <TextField label="Organización" value={config.identity.organization} onChange={(e) => update("identity", "organization", e.target.value)} fullWidth />
        <TextField label="Descripción del asistente" value={config.identity.description} onChange={(e) => update("identity", "description", e.target.value)} multiline minRows={2} fullWidth />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <TextField label="Tono" value={config.behavior.tone} onChange={(e) => update("behavior", "tone", e.target.value)} fullWidth />
          <TextField label="Extensión de respuestas" value={config.behavior.response_length} onChange={(e) => update("behavior", "response_length", e.target.value)} fullWidth />
        </Box>
      </Stack>
    </CardContent></Card>

    <Card sx={{ mb: 2 }}><CardContent>
      <Typography variant="h6" gutterBottom>Frases de respuesta inmediata</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Son mensajes breves que se muestran mientras el agente prepara una respuesta.
      </Typography>
      {[["default", "General"], ["system_guidance", "Manuales y orientación"], ["data_lookup", "Consulta de datos"], ["action_request", "Solicitud de acción"], ["general", "Otros mensajes"]].map(([key, label], index) => <React.Fragment key={key}>
        {index > 0 && <Divider sx={{ my: 2 }} />}
        <TextField label={label} helperText="Una frase por renglón; se elegirá una al azar." value={asLines(config.waiting_responses?.[key])} onChange={(e) => setConfig((current) => ({
          ...current,
          waiting_responses: { ...current.waiting_responses, [key]: e.target.value },
        }))} multiline minRows={2} fullWidth />
      </React.Fragment>)}
    </CardContent></Card>

      </AccordionDetails>
    </Accordion>
  </Box>;
}
